import { inject, Injectable } from '@angular/core';
import { AdminOrderCollectionFilter } from '../models/admin-order-collection-filter';
import {
  BehaviorSubject,
  catchError,
  defer,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { AdminOrderApiService } from '../api/services/admin-order-api.service';
import {
  CollectionFilterRequestBuilder,
  CollectionRequestBuilder,
} from '@web/common';
import { OrderStatus } from '@web/shared/module/shop';
import { blank, valueOrDefault } from '@web/utils';

@Injectable({
  providedIn: 'root',
})
export class AdminOrderStatsProvider {
  private readonly orderApi = inject(AdminOrderApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);

  /* prettier-ignore */
  private readonly countOfPaidOrdersSource$ = new BehaviorSubject<number>(0);

  public get countOfPaidOrders$(): Observable<number> {
    return defer(() => this.fetchCountOfPaidOrders()).pipe(
      tap((count) =>
        this.countOfPaidOrdersSource$.next(valueOrDefault(count, 0)),
      ),
      catchError(() => of(0)),
      switchMap(() => this.countOfPaidOrdersSource$.asObservable()),
    );
  }

  public refreshCountOfPaidOrders(): Observable<void> {
    return this.fetchCountOfPaidOrders().pipe(
      tap((items) => {
        this.countOfPaidOrdersSource$.next(items);
      }),
      blank(),
    );
  }

  private fetchCountOfPaidOrders(): Observable<number> {
    return this.orderApi
      .fetchCollection({
        request: this.collectionRequestBuilder
          .page(1)
          .pageSize(30)
          .filters(
            (
              builder: CollectionFilterRequestBuilder<AdminOrderCollectionFilter>,
            ) =>
              builder
                .appendText((builder) =>
                  builder.likeFor('status').value(OrderStatus.Paid).build(),
                )
                .build(),
          )
          .build(),
      })
      .pipe(map((response) => response.total));
  }
}
